"use client";

import { Button } from "@/components/ui/Button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/Dialog";
import {
  ArrowsPointingOutIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from "@heroicons/react/24/outline";
import Image from "next/image";
import { useEffect, useRef, useState } from "react";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import {
  Autoplay,
  EffectCoverflow,
  Navigation,
  Pagination,
} from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import ReactPlayer from "react-player";
import { useLanguage } from "@/context/LanguageContext";

const Demo = () => {
  const { language } = useLanguage();
  const copy = {
    cs: { previous: "Předchozí", next: "Další" },
    en: { previous: "Previous", next: "Next" },
  }[language];
  const [isImageOpen, setIsImageOpen] = useState(false);
  const [shouldPlayVideo, setShouldPlayVideo] = useState(false);
  const [selectedImage, setSelectedImage] = useState({ src: "", alt: "" });
  const videoSectionRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const section = videoSectionRef.current;

    if (!section) {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        setShouldPlayVideo(entry.isIntersecting);
      },
      {
        threshold: 0.6,
      },
    );

    observer.observe(section);

    return () => observer.disconnect();
  }, []);

  return (
    <div className="demo-root">
      <div ref={videoSectionRef} className="demo-carousel-wrap">
        <Swiper
          grabCursor
          centeredSlides
          slidesPerView={1}
          coverflowEffect={{
            rotate: 0,
            stretch: 0,
            depth: 80,
            modifier: 1,
            slideShadows: false,
          }}
          pagination={{ clickable: true }}
          navigation={{ nextEl: ".demo-next", prevEl: ".demo-prev" }}
          loop
          modules={[Navigation, EffectCoverflow, Pagination, Autoplay]}
          className="demo-swiper"
        >
          <SwiperSlide>
            <div className="relative aspect-video w-full overflow-hidden rounded-2xl bg-black">
              <ReactPlayer
                src="https://youtu.be/9HRrkw_-BOM"
                width="100%"
                height="100%"
                controls
                playing={shouldPlayVideo}
                playsInline
              />
            </div>
          </SwiperSlide>
          <SwiperSlide>
            <div className="relative aspect-video w-full overflow-hidden rounded-2xl bg-black">
              <ReactPlayer
                src="https://youtu.be/NyHUPIT_JGk"
                width="100%"
                height="100%"
                controls
                playing={false}
                playsInline
              />
            </div>
          </SwiperSlide>
        </Swiper>

        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="demo-prev demo-nav-btn"
          aria-label={copy.previous}
        >
          <ChevronLeftIcon className="w-20 h-20" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="demo-next demo-nav-btn"
          aria-label={copy.next}
        >
          <ChevronRightIcon className="w-20 h-20" />
        </Button>
      </div>

      <Dialog open={isImageOpen} onOpenChange={setIsImageOpen}>
        <DialogContent className="max-w-[90vw] max-h-[90vh] h-full w-full p-6 rounded-2xl">
          <DialogHeader>
            <DialogTitle className="text-sm font-medium text-softiq-copy dark:text-softiq-copy-inverse ">
              {selectedImage.alt}
            </DialogTitle>
          </DialogHeader>
          <Image
            src={selectedImage.src}
            alt={selectedImage.alt}
            fill
            sizes="90vw"
            loading="lazy"
            className="object-contain rounded-xl"
          />
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Demo;
